//
// Source code shamelessly stolen from link in Stripe.js, who attractively
// places their popup in the center of the merchant frame. In order to prevent
// popup blocking, we will do the same if the partner does not open the window
// before calling `sendClicked`
//

// Stolen from
// https://git.corp.stripe.com/stripe-internal/stripe-js-v3/blob/7713f2230bed287294cb7c05a57e0e55b787c540/src/linkPopup/libs/popup/helpers.ts#L1
export const getPopupPosition = ({
  outerHeight,
  outerWidth,
  screenY,
  screenX,
  popupHeight,
  popupWidth,
  availTop,
}: {
  outerHeight: number;
  outerWidth: number;
  screenY: number;
  screenX: number;
  popupHeight: number;
  popupWidth: number;
  availTop: number;
}) => {
  let top;
  // availTop is negative when we have a second screen above our primary screen
  // or when our window is move up so the top of the window is cut off
  if (availTop < 0) {
    top =
      availTop + // reset the origin to the top of the correct screen
      outerHeight / 2 +
      (Math.abs(availTop) + screenY) - // screenY is always negative due to the incorrect origin, reset it
      popupHeight / 2;
  } else {
    top = outerHeight / 2 + screenY - popupHeight / 2;
  }
  const left = outerWidth / 2 + screenX - popupWidth / 2;
  return {top, left};
};

// Stolen from
// https://git.corp.stripe.com/stripe-internal/stripe-js-v3/blob/7713f2230bed287294cb7c05a57e0e55b787c540/src/linkPopup/libs/popup/index.ts#L26
export const getCenteredPopupPosition = ({
  height,
  width,
}: {
  height: number;
  width: number;
}): {top: number; left: number} => {
  return getPopupPosition({
    outerHeight: window.outerHeight,
    outerWidth: window.outerWidth,
    screenY: window.screenY,
    screenX: window.screenX,
    popupHeight: height,
    popupWidth: width,
    // @ts-expect-error - https://developer.mozilla.org/en-US/docs/Web/API/Screen/availTop
    // eslint-disable-next-line @stripe-internal/stripe/no-props-suppress-errors
    availTop: window.screen.availTop || 0,
  });
};

// Stolen from
// https://git.corp.stripe.com/stripe-internal/stripe-js-v3/blob/7713f2230bed287294cb7c05a57e0e55b787c540/src/linkPopup/libs/popup/helpers.ts#L34
export const formatPopupOptions = (
  options: Record<string, string | number>,
): string => {
  return Object.keys(options)
    .map((key) => `${key}=${options[key]}`)
    .join();
};

type DimensionKeys = 'width' | 'height';
type ParsedOptions = Partial<Record<DimensionKeys, number>> & {
  [key: string]: string | number | undefined;
};

export const getCenteredPopupArgs = (
  args: Parameters<typeof window.open>,
): Parameters<typeof window.open> => {
  const [url, name, options] = args;
  const overrideOptions = getCenteredPopupOptions(options);
  return [url, name, overrideOptions];
};

const getCenteredPopupOptions = (options: string | undefined) => {
  if (!options) {
    return options;
  }

  const parsedOptions = parsePopupOptions(options);
  const {width, height} = parsedOptions;
  if (width === undefined || height === undefined) {
    return options;
  }

  return formatPopupOptions({
    ...parsedOptions,
    ...getCenteredPopupPosition({width, height}),
  });
};

export const parsePopupOptions = (options: string): ParsedOptions => {
  return options.split(',').reduce((acc, x) => {
    const [key, value] = x.trim().split('=');
    if (key === 'width' || key === 'height') {
      return {...acc, [key]: parseMaybeInt(value)};
    } else {
      return {...acc, [key]: parseMaybeInt(value) || value};
    }
  }, {} as ParsedOptions);
};

const parseMaybeInt = (value: string): number | undefined => {
  return hasOnlyDigits(value) ? parseInt(value, 10) : undefined;
};

const hasOnlyDigits = (value: string): boolean => {
  return /^-?\d+$/.test(value);
};
